import React, { Component } from "react";
import styled from 'styled-components';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Button, Grid, Icon, Modal, Segment } from 'semantic-ui-react';
import { FormState } from 'final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { GuarantorDto, UpdateGuarantorsMedIndexRequest } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import { debounce } from 'ts-debounce';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import Input from 'components/final-form/Input';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import { searchUpdateGuarantors, updateGuarantorMedIndex } from 'services/guarantorServices';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import MedIndexAddView from 'components/guarantors/MedIndexAddView';

const StyledGrid = styled(Grid)`
  flex: 1 0 200px;
`;

const StyledGuarantor = styled(Segment)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledGuarantorTableContainer = styled.div`
  height: 97%;
`;

const ButtonsDivider = styled.div`
  margin-top: 5rem;
`;

const TitlesDivider = styled.div`
  margin-bottom: 1rem;
`;

interface GuarantorUpdateSearchRequest {
  description: string;
  city: string;
  tariffType: string;
  includesInactive: boolean;
}

interface GuarantorFieldProps extends FormState {
  values: Partial<GuarantorUpdateSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, GuarantorsConsumerRenderProps, NotificationSystemProps {
}

interface State {
  foundGuarantors: Array<GuarantorDto>,
  guarantorsLoaded: boolean,
  updateFinished: boolean,
  guarantors: Array<GuarantorDto>,
  addMedIndexModalOpen: boolean,
  searchValues?: Partial<GuarantorUpdateSearchRequest>,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class GuarantorUpdateView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundGuarantors: [],
      guarantorsLoaded: false,
      updateFinished: true,
      guarantors: [],
      addMedIndexModalOpen: false,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  showModal = () => {
    this.setState({
      addMedIndexModalOpen: true
    });
  };

  hideModal = (importing?: boolean) => {
    const {searchValues} = this.state;
    if (importing && searchValues) {
      this.guarantorSearch(searchValues);
    }

    this.setState({
      addMedIndexModalOpen: false
    });
  };

  initialFormValues = () => {
    return {
      description: "",
      includesInactive: false
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  handleError = () => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  handleSubmit = () => {

    const {selectionNotificationSystem, currentUser, getMessage} = this.props;
    const {foundGuarantors, cancelTokenSource, searchValues} = this.state;

    this.setState({
      updateFinished: false
    });

    let selectedGuarantors = foundGuarantors.filter(g => g.selection);

    const guarantorIds: Array<number> = selectedGuarantors.map(guarantor => guarantor.id);

    const request: UpdateGuarantorsMedIndexRequest = {
      mandateId: currentUser!.userConfig.mandateId,
      updateGuarantorIds: guarantorIds,
      updateOnly: true
    }

    const onFinally = () => {
      if(searchValues){
        this.guarantorSearch(searchValues);
      }
      this.setState({
        updateFinished: true
      })
    };

    updateGuarantorMedIndex(request, cancelTokenSource)
        .then(response => {
          //@ts-ignore
          const notification = selectionNotificationSystem.current;
          if (notification) {
            notification.addNotification({
              title: getMessage("guarantor.update.success"),
              message: getMessage("guarantorUpdate.successfulUpdate"),
              level: 'success'
            });
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  }

  handleDownload = () => {

    const {currentUser, getMessage} = this.props;
    const {cancelTokenSource, searchValues} = this.state;

    this.setState({
      updateFinished: false
    });

    const request: UpdateGuarantorsMedIndexRequest = {
      mandateId: currentUser!.userConfig.mandateId,
      updateGuarantorIds: [],
      updateOnly: false
    }

    const onFinally = () => {
      if(searchValues){
        this.guarantorSearch(searchValues);
      }
      this.setState({
        updateFinished: true
      })
    };

    updateGuarantorMedIndex(request, cancelTokenSource)
        .then(response => {
          //@ts-ignore
          const notification = selectionNotificationSystem.current;
          if (notification) {
            notification.addNotification({
              title: getMessage("guarantor.update.success"),
              message: getMessage("guarantorUpdate.successfulUpdate"),
              level: 'success'
            });
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  }

  guarantorSearch = (values: Partial<GuarantorUpdateSearchRequest>) => {

    const {cancelTokenSource} = this.state;

    this.setState({
      searchValues: values,
      guarantorsLoaded: false
    });

    const onFinally = () => {
      this.setState({
        guarantorsLoaded: true
      })
    };

    let valuesCopy = {...values};

    if (valuesCopy.description === undefined) {
      valuesCopy.description = "";
    }

    if (valuesCopy.city === undefined) {
      valuesCopy.city = "";
    }

    searchUpdateGuarantors(valuesCopy, cancelTokenSource)
        .then(response => {

          this.setState({
            foundGuarantors: response
          });
        })
        .catch(noop)
        .finally(onFinally);
  }

  handleChange = debounce(({values}: GuarantorFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    this.guarantorSearch(values);
  }, 300);

  render(): React.ReactNode {

    return (
        <React.Fragment>
          {this.renderSearchGuarantorSegment()}
          {this.renderMedIndexAddModal()}
        </React.Fragment>
    );
  }

  renderSearchGuarantorSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialFormValues()}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    )

  }

  renderMedIndexAddModal = () => {

    const {getMessage} = this.props;
    const {addMedIndexModalOpen} = this.state;

    return (
        <Modal
            open={addMedIndexModalOpen}
            onClose={() => this.hideModal(false)}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("guarantorUpdate.addMedIndex")}</Modal.Header>
          <FormModalContentContainer>
            <MedIndexAddView
                onClose={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )

  }

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;

    const {guarantorsLoaded, updateFinished, errorMessage, foundGuarantors} = this.state;

    const disabled = foundGuarantors.filter(g => g.selection).length === 0;

    const numResults = foundGuarantors.length;
    const selectedResults = foundGuarantors.filter(guarantor => guarantor.selection).length;

    const loaderMessage = !guarantorsLoaded
        ? getMessage("guarantor.loading")
        : !updateFinished ? getMessage("guarantorUpdate.updating") : "";

    return (
        <StyledGuarantor>
          <div>
            <PraxFormGrid>
              {errorMessage
              && (
                  <Grid.Row>
                    <Grid.Column>
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                    </Grid.Column>
                  </Grid.Row>
              )
              }
              <Grid.Row>
                <Grid.Column width={3} verticalAlign="middle">
                  {getMessage("description")}:
                </Grid.Column>
                <Grid.Column width={13}>
                  <Field
                      name="description"
                      component={Input}
                      autoFocus
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={3} verticalAlign="middle">
                  {getMessage("city")}:
                </Grid.Column>
                <Grid.Column width={13}>
                  <Field
                      name="city"
                      component={Input}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={3} verticalAlign="middle">
                </Grid.Column>
                <Grid.Column width={4} verticalAlign={"middle"}>
                  <Field
                      name="includesInactive"
                      component={CheckBox}
                      label={getMessage("includingInactive")}
                      toggle={true}
                  />
                </Grid.Column>
                <Grid.Column width={9} textAlign="right">
                  <Button compact type="submit" disabled={disabled} onClick={this.handleSubmit} primary>
                    {getMessage("guarantorUpdate.update")}
                  </Button>
                  <Button compact type="submit" onClick={this.handleDownload} primary>
                    {getMessage("guarantorUpdate.download")}
                  </Button>
                  <Button compact type="button" onClick={this.showModal} primary>
                    {getMessage("guarantorUpdate.addMedIndex")}
                  </Button>
                </Grid.Column>
              </Grid.Row>
              <SearchResultComponent numResults={numResults} numSelected={selectedResults} textMessage={"guarantor.update.selectedGuarantors"}/>
              <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
            </PraxFormGrid>
          </div>
          <TitlesDivider/>
          <StyledGrid>
            <Grid.Row>
              <Grid.Column width={16}>
                <StyledGuarantorTableContainer>
                  {!guarantorsLoaded || !updateFinished
                      ? this.renderGuarantorLoader(loaderMessage)
                      : this.renderGuarantorSegment()}
                </StyledGuarantorTableContainer>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
        </StyledGuarantor>
    )

  }

  selectGuarantor = (guarantorId: number, index: number) => {
    const {foundGuarantors} = this.state;

    const guarantor = foundGuarantors.find(guarantor => guarantor.id === guarantorId);
    if (guarantor) {
      guarantor.selection = !guarantor.selection;

      foundGuarantors[index] = guarantor;
      this.setState({
        foundGuarantors: foundGuarantors
      })

    }
  };

  foundGuarantorRowGetter = ({index}: any) => {
    const {foundGuarantors} = this.state;
    return foundGuarantors[index];
  };

  foundGuarantorRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundGuarantors} = this.state;

    if (!foundGuarantors[index].selection) {
      style.color = '#c6c6cb';
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.selectGuarantor(foundGuarantors[index].id, index)}
        >
          {columns}
        </div>
    );
  };

  guarantorSelectCellRenderer = (data: any) => {

    const {rowIndex, rowData, cellData} = data;

    return (
        <CheckboxSc
            checked={cellData}
            onChange={() => this.selectGuarantor(rowData.id, rowIndex)}
        />
    )
  };

  selectHeaderColumn = () => {

    const {foundGuarantors} = this.state;
    const {getMessage} = this.props;

    const countSelectedGuarantors = foundGuarantors.filter(g => g.selection).length;
    const indeterminate = countSelectedGuarantors > 0 && countSelectedGuarantors < foundGuarantors.length;
    const noSelectedGuarantors = countSelectedGuarantors === 0;
    const allGuarantorsSelected = countSelectedGuarantors === foundGuarantors.length;

    return (
        <div>
          <CheckboxSc
              label={getMessage("select")}
              checked={countSelectedGuarantors}
              indeterminate={indeterminate}
              onChange={() => {
                if (noSelectedGuarantors || indeterminate) {
                  const selectedGuarantors = foundGuarantors.map(guarantor => {
                    return {
                      ...guarantor,
                      selection: true
                    }
                  })
                  this.setState({
                    foundGuarantors: selectedGuarantors
                  })
                }

                if (allGuarantorsSelected) {
                  const guarantors = foundGuarantors.map(guarantor => {
                    return {
                      ...guarantor,
                      selection: false
                    }
                  })
                  this.setState({
                    foundGuarantors: guarantors
                  })
                }
              }}
              disabled={!foundGuarantors.length}
          />
        </div>
    )
  }

  tariffTypeCellRenderer = ({cellData}: any) => {

    const {getMessage} = this.props;

    if (cellData) {

      return getMessage(`tariffType.${cellData.toLowerCase()}`)
    }
  }

  notificationCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  renderGuarantorSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundGuarantors} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundGuarantors.length}
            rowGetter={this.foundGuarantorRowGetter}
            rowRenderer={this.foundGuarantorRowRenderer}
            columns={[
              {
                width: 100,
                label: this.selectHeaderColumn(),
                dataKey: "selection",
                cellRenderer: this.guarantorSelectCellRenderer
              },
              {
                width: 200,
                label: (getMessage("eanNumber")),
                dataKey: "eanNumber"
              },
              {
                width: 200,
                label: (getMessage("bagNumber")),
                dataKey: "bagNumber"
              },
              {
                width: 200,
                label: (getMessage("tariff")),
                dataKey: "tariffType",
                cellRenderer: (this.tariffTypeCellRenderer)
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 200,
                label: (getMessage("city")),
                dataKey: "city"
              },
              {
                width: 200,
                label: (getMessage("street")),
                dataKey: "street"
              },
              {
                width: 100,
                label: (getMessage("active")),
                dataKey: "active",
                cellRenderer: this.notificationCellRenderer
              }
            ]}
        />
    );
  };

  renderGuarantorLoader = (message: string) => {
    return (
        <LoaderComponent message={message}/>
    )
  }

}

const GuarantorUpdateViewWrapper =
    withMessages(
        withAuthContext(
            withGuarantorsContext(
                withNotificationSystemContext(
                    GuarantorUpdateView))));


export default GuarantorUpdateViewWrapper;
